<script setup lang="ts">
import { computed } from 'vue'
import { effectiveUI, currentLanguage, pageId, languages, t } from '@/i18n'

// Get current language config
const langConfig = computed(() => languages[currentLanguage.value])

// Get the URL for a spoke/hub page
function getPageUrl(slug: string): string {
  const lang = langConfig.value
  const langPrefix = lang.path || `/${lang.code}`
  return `${langPrefix}/${slug}/`
}

// Hub page URL
const hubUrl = computed(() => {
  const lang = langConfig.value
  return lang.path ? `${lang.path}/` : '/'
})

// Links to show: on hub, show all spokes; on spoke, show hub + other spokes
const links = computed(() => {
  const spokeLinks = langConfig.value.spokeLinks || []
  const currentPageId = pageId.value

  const result: Array<{ href: string; label: string; description: string }> = []

  // On spoke pages, add link back to hub (use base hub UI, not spoke-merged effectiveUI)
  if (currentPageId !== 'hub') {
    result.push({
      href: hubUrl.value,
      label: t.value.title,
      description: t.value.subtitle
    })
  }

  // Add spoke links (excluding current spoke)
  for (const link of spokeLinks) {
    if (link.slug !== currentPageId) {
      result.push({
        href: getPageUrl(link.slug),
        label: link.label,
        description: link.description
      })
    }
  }

  return result
})

const hasLinks = computed(() => links.value.length > 0)
</script>

<template>
  <nav v-if="hasLinks" class="spoke-links-section" aria-label="Related tools">
    <h2 class="spoke-links-title">{{ effectiveUI.relatedToolsTitle }}</h2>
    <div class="spoke-links-grid">
      <a
        v-for="link in links"
        :key="link.href"
        :href="link.href"
        class="spoke-link-card"
      >
        <span class="spoke-link-label">{{ link.label }}</span>
        <span class="spoke-link-desc">{{ link.description }}</span>
      </a>
    </div>
  </nav>
</template>

<style scoped>
.spoke-links-section {
  margin-top: 3rem;
  margin-bottom: 2rem;
}

.spoke-links-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.spoke-links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  max-width: 960px;
  margin: 0 auto;
}

.spoke-link-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  text-decoration: none;
  transition: border-color var(--transition-normal), box-shadow var(--transition-normal);
}

.spoke-link-card:hover {
  border-color: var(--primary-color);
  box-shadow: var(--hover-shadow);
}

.spoke-link-label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.spoke-link-desc {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.4;
}

@media (max-width: 640px) {
  .spoke-links-title {
    font-size: 1.25rem;
  }

  .spoke-links-grid {
    grid-template-columns: 1fr;
  }

  .spoke-link-card {
    padding: 0.875rem 1rem;
  }
}
</style>
